import { Type } from "@sinclair/typebox";
import { TypeBoxToType } from "@whatslly/types/TypeBoxToType";
import { ApiErrorResponse } from "../ApiErrorResponse";

const WabaTemplateComponent = Type.Union([
  Type.Object({
    type: Type.Literal("HEADER"),
    format: Type.String(),
    text: Type.Optional(Type.String()),
    example: Type.Optional(
      Type.Object({
        header_text: Type.Optional(Type.Array(Type.String())),
        header_handle: Type.Optional(Type.Array(Type.String())),
      })
    ),
  }),
  Type.Object({
    type: Type.Literal("BODY"),
    text: Type.String(),
    example: Type.Optional(Type.Object({ body_text: Type.Array(Type.Array(Type.String())) })),
  }),
  Type.Object({
    type: Type.Literal("FOOTER"),
    text: Type.String(),
  }),
  Type.Object({
    type: Type.Literal("BUTTONS"),
    buttons: Type.Array(
      Type.Object({
        type: Type.String(),
        text: Type.String(),
        example: Type.Optional(Type.Array(Type.String())),
        flow_id: Type.Optional(Type.String()),
      })
    ),
  }),
]);
export type WabaTemplateComponent = TypeBoxToType<typeof WabaTemplateComponent>;

export const WabaTemplate = Type.Object({
  name: Type.String(),
  components: Type.Array(WabaTemplateComponent),
  language: Type.String(),
  status: Type.Union([Type.Literal("APPROVED"), Type.Literal("PENDING"), Type.Literal("REJECTED")]),
  id: Type.String(),
});
export type WabaTemplate = TypeBoxToType<typeof WabaTemplate>;

export enum TemplatePurpose {
  OPT_IN = "opt-in",
  CAMPAIGN = "campaign",
}

export const TemplatePurposeSchema = Type.Enum(TemplatePurpose);

const TemplateLanguages = Type.Object({
  name: Type.String(),
  languages: Type.Array(
    Type.Object({
      id: Type.String(),
      language: Type.String(),
      components: Type.Array(WabaTemplateComponent),
      status: Type.Union([Type.Literal("APPROVED"), Type.Literal("PENDING"), Type.Literal("REJECTED")]),
    })
  ),
  groupIds: Type.Optional(Type.Array(Type.String())),
  purposes: Type.Optional(Type.Array(TemplatePurposeSchema)),
});
export type TemplateLanguages = TypeBoxToType<typeof TemplateLanguages>;

export const WhatsappBusinessApiGetTemplatesQueryString = Type.Object({
  orgId: Type.String(),
  approvedOnly: Type.Optional(Type.Boolean()),
  onlySelectedTemplates: Type.Optional(Type.Boolean()),
});

export type WhatsappBusinessApiGetTemplatesQueryString = TypeBoxToType<typeof WhatsappBusinessApiGetTemplatesQueryString>;

const WhatsappBusinessApiGetTemplatesResponseSuccess = Type.Object({
  success: Type.Literal(true),
  templates: Type.Array(TemplateLanguages),
  waba_template_ids: Type.Array(Type.String()),
});
export const WhatsappBusinessApiGetTemplatesResponse = Type.Union([ApiErrorResponse, WhatsappBusinessApiGetTemplatesResponseSuccess]);
export type WhatsappBusinessApiGetTemplatesResponse = TypeBoxToType<typeof WhatsappBusinessApiGetTemplatesResponse>;

export const WhatsappBusinessApiAssignTemplateBody = Type.Object({
  orgId: Type.String(),
  addTemplateIds: Type.Array(Type.String()),
  removeTemplateIds: Type.Array(Type.String()),
});
export type WhatsappBusinessApiAssignTemplateBody = TypeBoxToType<typeof WhatsappBusinessApiAssignTemplateBody>;

export const WhatsappBusinessApiAssignTemplateResponseSuccess = Type.Object({ success: Type.Literal(true) });
export const WhatsappBusinessApiAssignTemplateResponse = Type.Union([ApiErrorResponse, WhatsappBusinessApiAssignTemplateResponseSuccess]);
export type WhatsappBusinessApiAssignTemplateResponse = TypeBoxToType<typeof WhatsappBusinessApiAssignTemplateResponse>;

export const WhatsappBusinessApiBaseHeaderType = Type.Union([Type.Literal("NONE"), Type.Literal("TEXT")]);
export type WhatsappBusinessApiBaseHeaderType = TypeBoxToType<typeof WhatsappBusinessApiBaseHeaderType>;
export const WhatsappBusinessApiMediaHeaderType = Type.Union([Type.Literal("IMAGE"), Type.Literal("VIDEO"), Type.Literal("DOCUMENT")]);
export type WhatsappBusinessApiMediaHeaderType = TypeBoxToType<typeof WhatsappBusinessApiMediaHeaderType>;

export const WhatsappBusinessApiHeaderType = Type.Union([WhatsappBusinessApiBaseHeaderType, WhatsappBusinessApiMediaHeaderType]);
export type WhatsappBusinessApiHeaderType = TypeBoxToType<typeof WhatsappBusinessApiHeaderType>;

export type TemplateHeaderMediaType = Lowercase<WhatsappBusinessApiMediaHeaderType>;

export type EditeableComponent = {
  type: WabaTemplateComponent["type"];
  sub_type?: string;
  index?: number;
  format?: string;
  text: string;
  example: string[];
  values: Record<number, string>;
};

export const editableComponentToParameter = (component: EditeableComponent) => {
  switch (component.type) {
    case "BUTTONS":
      return {
        type: "BUTTON",
        sub_type: component.sub_type,
        index: component.index,
        parameters: component.example.map((_, index) => ({ type: "payload" as const, payload: component.values[index + 1] })),
      };
    default:
      return {
        type: component.type.toLowerCase(),
        parameters: component.example.map((_, index) => ({ type: "text" as const, text: component.values[index + 1] })),
      };
  }
};

/** Button sub_types that Meta requires in the request even without parameters */
const REQUIRED_BUTTON_SUB_TYPES = new Set(["FLOW", "COPY_CODE"]);

/**
 * Returns components that must be included in the template send request,
 * including buttons like FLOW that have no editable parameters but are
 * required by the WhatsApp API.
 */
export const getRequiredComponents = (components: WabaTemplateComponent[] | undefined): ReturnType<typeof editableComponentToParameter>[] => {
  if (!components) return [];
  const result: ReturnType<typeof editableComponentToParameter>[] = [];
  for (const component of components) {
    if (component.type !== "BUTTONS") continue;
    for (let i = 0; i < component.buttons.length; i++) {
      const button = component.buttons[i];
      if (REQUIRED_BUTTON_SUB_TYPES.has(button.type) && (!button.example || button.example.length === 0)) {
        result.push({
          type: "BUTTON",
          sub_type: button.type,
          index: i,
          parameters: [],
        });
      }
    }
  }
  return result;
};

export const componentsToEditableComponents = (
  components: WabaTemplateComponent[] | undefined,
  parameters?: ReturnType<typeof editableComponentToParameter>[]
): EditeableComponent[] =>
  components?.flatMap((component) => {
    const values = parameters
      ?.find((parameter) => parameter.type?.toLowerCase() === component.type?.toLowerCase())
      ?.parameters?.reduce(
        (acc, parameter, index) => ({ ...acc, [index + 1]: parameter.type === "payload" ? parameter.payload : parameter.text }),
        {} as Record<number, string>
      );
    return copmententToEditableComponent(component, values);
  }) ?? [];

const copmententToEditableComponent = (
  component: WabaTemplateComponent,
  values?: Record<number, string>
): EditeableComponent | EditeableComponent[] => {
  switch (component.type) {
    case "HEADER":
      return {
        type: component.type,
        format: component.format,
        text: component.text ?? "",
        example: component.example?.header_text ?? [],
        values: values ?? component.example?.header_text?.reduce((acc, _, index) => ({ ...acc, [index + 1]: "" }), {}) ?? {},
      };
    case "BODY":
      return {
        type: component.type,
        text: component.text,
        example: component.example?.body_text[0] ?? [],
        values: values ?? component.example?.body_text[0]?.reduce((acc, _, index) => ({ ...acc, [index + 1]: "" }), {}) ?? {},
      };
    case "BUTTONS":
      return component.buttons.map((button, index) => ({
        type: component.type,
        sub_type: button.type,
        index,
        text: button.text,
        example: button.example ?? [],
        values: values ?? button.example?.reduce((acc, _, index) => ({ ...acc, [index + 1]: "" }), {}) ?? {},
      }));
    case "FOOTER":
      return {
        type: component.type,
        text: component.text,
        example: [],
        values: values ?? {},
      };
  }
};

export interface TemplateAnalysis {
  bodyParamCount: number;
  headerTextParamCount: number;
  buttonParamCount: number;
}

export function analyzeTemplate(components: WabaTemplateComponent[]): TemplateAnalysis {
  const header = components.find((c) => c.type === "HEADER");
  const body = components.find((c) => c.type === "BODY");
  const buttons = components.find((c) => c.type === "BUTTONS");

  return {
    bodyParamCount: body && "example" in body ? body.example?.body_text?.[0]?.length || 0 : 0,
    headerTextParamCount: header && "example" in header ? header.example?.header_text?.length || 0 : 0,
    buttonParamCount: buttons && "buttons" in buttons ? buttons.buttons.reduce((acc, button) => acc + (button.example?.length || 0), 0) : 0,
  };
}

export function getHeaderMediaType(components: WabaTemplateComponent[] | undefined): WhatsappBusinessApiHeaderType | undefined {
  const header = components?.find((c) => c.type === "HEADER");
  if (!header || header.type !== "HEADER") return undefined;
  const format = header.format?.toUpperCase();
  if (format === "IMAGE" || format === "VIDEO" || format === "DOCUMENT") return format;
  return undefined;
}

function buildMediaHeaderParameter(mediaType: TemplateHeaderMediaType, link: string) {
  if (mediaType === "image") return { type: "image" as const, image: { link } };
  if (mediaType === "video") return { type: "video" as const, video: { link } };
  return { type: "document" as const, document: { link } };
}

export function injectMediaHeaderComponent<T extends { type: string }>(
  components: readonly T[],
  mediaType: TemplateHeaderMediaType,
  mediaUrl: string
) {
  const headerComponent = { type: "header", parameters: [buildMediaHeaderParameter(mediaType, mediaUrl)] };
  const hasHeader = components.some((c) => c.type.toLowerCase() === "header");
  if (!hasHeader) return [headerComponent, ...components];
  return components.map((c) => (c.type.toLowerCase() === "header" ? headerComponent : c));
}

export function buildTemplateSendComponents(
  templateComponents: WabaTemplateComponent[],
  rowParameters: Record<string, string>
): ReturnType<typeof editableComponentToParameter>[] {
  const result: ReturnType<typeof editableComponentToParameter>[] = [];
  const editableComponents = componentsToEditableComponents(templateComponents);
  let buttonParamOffset = 0;

  for (const comp of editableComponents) {
    if (comp.example.length === 0) continue;

    const values: Record<number, string> = {};
    for (let i = 0; i < comp.example.length; i++) {
      const paramKey =
        comp.type === "HEADER" ? `header_param${i + 1}` : comp.type === "BODY" ? `param${i + 1}` : `button_param${buttonParamOffset + i + 1}`;
      values[i + 1] = rowParameters[paramKey] || "";
    }

    if (comp.type === "BUTTONS") buttonParamOffset += comp.example.length;
    result.push(editableComponentToParameter({ ...comp, values }));
  }

  return result;
}
